// 「← ゲーム一覧へ」（`index.html` の `#home-btn`）を**キャンバスに追従**させる。
//
// ⚠️ Phaser を値として import しないこと（[main.ts](../../main.ts) の注記と同じ理由）。
import type Phaser from 'phaser';

/**
 * キャンバス左上からの余白（**設計座標**）。
 * 追従後はこの値がそのまま画面の見た目に反映される（倍率を掛けて CSS px にする）。
 */
export const HOME_BUTTON_MARGIN = 8;

/**
 * ゲーム中の「← ゲーム一覧へ」がキャンバスの**どこを覆うか**（設計座標の右端・下端）。
 *
 * `trackHomeButton` がボタンをキャンバスと同じ倍率で拡縮するので、**窓の形が変わっても
 * この値は動かない**。ゲーム側はこの右側にだけ物を置けばよい（`sceneChrome` が使う）。
 *
 * 値は ja の「← ゲーム一覧へ」（124×38 CSS px ＝ 倍率1のときの実測）＋余白8。
 * en の `← Game list` はこれより狭いので、広いほうで押さえておけば両方安全。
 */
export const HOME_BUTTON_RIGHT = HOME_BUTTON_MARGIN + 124;
export const HOME_BUTTON_BOTTOM = HOME_BUTTON_MARGIN + 38;

/**
 * 画面**最上部の帯**の高さ（設計座標）。ここに押し代を置いてはいけない。
 *
 * 🔴 **iPadOS のウインドウ操作がこの帯を取る**（2026-08-18 に本番 iPad で発生）。
 *   ドラッグで移動、**ダブルタップでサイズ変更モード**（赤×/黄−/緑が出る）になるので、
 *   canvas 側のボタンを置くと**指が OS に持っていかれる**。
 *   症状は「ボタンの反応が悪い」＝ 押せる回と押せない回があるという形で出る。
 *
 * ⚠️ **2026-08-17 に直したのは横方向**（DOM のホームボタンとの重なり）で、
 *   **縦方向は手つかずだった**。当時 4本とも押し代の上端は
 *   y = −1（箱プッシュ/トランプ/2048）または 10（Triangle Sweeper）＝ 帯のど真ん中。
 *   見た目の三角は帯の中で完結しているので、**目視でもスクリーンショットでも見つからない**。
 *
 * ⇒ 戻るボタンは**この値より下**へ置く（`backButtonHitArea.test.ts` が見張る）。
 *   実際には各ゲームの見出し帯（`HEADER_H`）のほうが厚いので、
 *   「見出し帯の下」に置けば自動的に満たされる。**薄い帯のゲームを足したとき用の下限**。
 *
 * ⚠️ **DOM のホームボタン（y = 8..46）もこの帯の中にある**。同じ理由で取られうるので、
 *   2026-08-18 に**タイトル画面だけに出す**ことにした（`HOME_BUTTON_VISIBLE_CLASS`）。
 *   遊んでいる最中の画面からは帯の占有物が無くなったが、**タイトル画面では帯の中のまま**。
 *   タイトルは他に押すものが無い画面なので取り違えは起きにくい、という前提で置いている。
 */
export const OS_TOP_STRIP_H = 50;

/**
 * 「← ゲーム一覧へ」を**実際に見せる**ときに付くクラス。
 *
 * 🔴 **タイトル画面だけに出す**（2026-08-18 ユーザー指示）。`body.in-game` だけだと
 *   遊んでいる最中もずっと左上に居座る。左上は
 *   **DOM のホームボタンと OS の帯が二重に効く一等地**で、実際そこに置いた戻るボタンで
 *   2日続けて事故を起こした（`OS_TOP_STRIP_H` の注記）。出番を入口画面に絞れば、
 *   プレイ中の画面から帯の占有物そのものが無くなる。
 *
 * ⚠️ **[index.html](../../../index.html) の CSS と同じ綴りであること**。
 *   CSS は定数を import できないので、片方だけ変えると**黙って出なくなる**
 *   （型でもテストでも気づけない）。`homeButtonVisibility.test.ts` が両方を読んで突き合わせる。
 */
export const HOME_BUTTON_VISIBLE_CLASS = 'at-entry';

/**
 * 「← ゲーム一覧へ」をキャンバスの左上へ貼り付け、**キャンバスと同じ倍率で拡縮**する。
 * 戻り値を呼ぶと元の CSS（ビューポート基準）へ戻る。
 *
 * ## なぜ要るか（2026-08-05 に踏んだ）
 *
 * 素の CSS は `position: fixed; top/left: 8px` ＝ **ビューポート基準**。一方キャンバスは
 * Phaser の Scale.FIT で **4:3 を保ったままレターボックス**される。基準が違うので、
 * 「ボタンがキャンバスのどこに乗るか」が**窓の形で変わってしまう**（実測・ja）:
 *
 * | ビューポート | アスペクト | キャンバス | ボタンの設計座標 x |
 * |---|---|---|---|
 * | 1400×780 | 1.795 | 左右に黒帯(left=270) | −258〜−136（完全に外） |
 * | 1200×800 | 1.500 | 左右に黒帯(left=100) | −88〜31 |
 * | **1200×900** | **1.333** | **黒帯なし** | 7〜**113**（最悪） |
 * | 1200×1000 | 1.200 | 上下に黒帯(top=75) | y が −57〜−25（キャンバスの上） |
 *
 * Chrome と Safari はツールバーの高さが違う＝**同じ窓の大きさでもアスペクトが変わる**ので、
 * 「片方のブラウザでだけ戻るボタンが押せない」という形で表に出た。
 * しかも安全な固定 x は無い（黒帯ゼロのとき右端 = 132×1024÷ビューポート幅 なので、
 * 窓が狭いほど右へ伸びる）。
 *
 * そこで**ボタンのほうをキャンバス基準に寄せて**、占有範囲を設計座標で固定する。
 */
export function trackHomeButton(
  btn: HTMLElement,
  game: Phaser.Game,
  entrySceneKey: string,
): () => void {
  const apply = (): void => {
    const r = game.canvas.getBoundingClientRect();
    if (r.width === 0) return; // 破棄済み / 非表示（このあと resize で呼び直される）
    const scale = r.width / game.scale.baseSize.width;
    btn.style.left = `${r.left + HOME_BUTTON_MARGIN * scale}px`;
    btn.style.top = `${r.top + HOME_BUTTON_MARGIN * scale}px`;
    btn.style.transformOrigin = 'top left';
    btn.style.transform = `scale(${scale})`;
  };

  /**
   * 入口シーン（タイトル）に居るときだけ見せる。
   *
   * 🔴 **シーン名を直書きしない**。`ENTRY_SCENE_KEY` は runtime が名乗るので、
   *   ここで 'Title' と書くと platform がゲームを名指しすることになる
   *   （CLAUDE.md の「依存の向きは games → platform のみ」）。
   *
   * ⚠️ 監視は**毎フレームの状態比較**にしてある。各シーンの 'start'/'shutdown' を
   *   個別に購読すると、**後から足したシーンを取りこぼす**（登録漏れは画面に出ず、
   *   「あの画面だけボタンが残る」という形で後から見つかる）。
   *   `isActive` は Map 引き1回で、変化したときだけ DOM を触る。
   *
   * 🔴 **今の状態は DOM から読む**（`let shown` のようなキャッシュを持たない）。
   *   キャッシュにすると、**外からクラスを外されたときに食い違う**。
   *   実際に踏んだ（2026-08-18）: `exitToDashboard` がクラスを落とす一行を足したところ、
   *   閉包の `shown` が true のまま残り、同じゲームへ入り直しても
   *   「変化なし」と判断して**タイトルでボタンが出なくなった**。
   *   ⚠️ ブラウザで通しに触って初めて出た＝単体テストでは見つからない形。
   */
  const syncVisibility = (): void => {
    const on = game.scene.isActive(entrySceneKey);
    if (on === btn.classList.contains(HOME_BUTTON_VISIBLE_CLASS)) return;
    btn.classList.toggle(HOME_BUTTON_VISIBLE_CLASS, on);
    // 隠れている間に窓が変わっているかもしれないので、出す瞬間に測り直す。
    if (on) apply();
  };

  apply();
  syncVisibility();
  // ⚠️ イベント名は**文字列リテラル**で書く（`Phaser.Scale.Events.RESIZE` は値なので、
  //    参照すると初期バンドルに Phaser 本体が入る）。
  game.scale.on('resize', apply);
  // Scale.FIT は窓の変化で resize を投げるが、ブラウザのUI伸縮など投げない場面もあるので併用。
  window.addEventListener('resize', apply);
  game.events.on('poststep', syncVisibility);

  return () => {
    game.scale.off('resize', apply);
    window.removeEventListener('resize', apply);
    game.events.off('poststep', syncVisibility);
    btn.classList.remove(HOME_BUTTON_VISIBLE_CLASS);
    btn.style.left = '';
    btn.style.top = '';
    btn.style.transform = '';
    btn.style.transformOrigin = '';
  };
}
